import React, { useEffect, useState } from 'react';
import { Menu, X, Leaf, Recycle, Users, TrendingUp, Droplet, Shield, ChevronDown } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_green-fibre-banana/artifacts/7rucq3ae_bansatra.jpeg';

const Home = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home');
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });

  useEffect(() => {
    const handleScroll = () => {
      const sections = ['home', 'about', 'features', 'process', 'vision', 'gallery', 'contact'];
      const scrollPosition = window.scrollY + 100;

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
    setMobileMenuOpen(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.message) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields.",
        variant: "destructive"
      });
      return;
    }

    // Frontend-only form submission
    toast({
      title: "Message Sent!",
      description: "Thank you for contacting us. We'll get back to you soon.",
    });

    setFormData({ name: '', email: '', message: '' });
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const navLinks = [
    { id: 'home', label: 'Home' },
    { id: 'about', label: 'About' },
    { id: 'features', label: 'Features' },
    { id: 'process', label: 'Process' },
    { id: 'vision', label: 'Vision' },
    { id: 'gallery', label: 'Gallery' },
    { id: 'contact', label: 'Contact' }
  ];

  return (
    <div className="banastra-website">
      {/* Header Navigation */}
      <header className="network-header">
        <div className="nav-wrapper">
          <div className="network-logo-container" onClick={() => scrollToSection('home')}>
            <img src={LOGO_URL} alt="Banastra Logo" className="logo-image" />
          </div>

          {/* Desktop Navigation */}
          <nav className="network-nav desktop-nav">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`network-nav-link ${activeSection === link.id ? 'active' : ''}`}
              >
                {link.label}
              </button>
            ))}
          </nav>

          {/* Mobile Menu Toggle */}
          <button className="mobile-menu-toggle" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {mobileMenuOpen && (
          <div className="mobile-nav">
            {navLinks.map((link) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className="mobile-nav-link"
              >
                {link.label}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* Hero Section */}
      <section id="home" className="hero-section">
        <div className="hero-overlay"></div>
        <div className="hero-content">
          <div className="hero-text">
            <h1 className="hero-title animated fadeIn">
              Turning Agricultural Waste into Sustainable Fashion
            </h1>
            <p className="hero-subtitle animated fadeIn delay-200ms">
              Transforming banana stem waste into eco-friendly, biodegradable textile fibre
            </p>
            <div className="hero-buttons animated fadeIn delay-500ms">
              <Button 
                className="btn-primary btn-cta"
                onClick={() => scrollToSection('about')}
              >
                Learn More
              </Button>
              <Button 
                className="btn-secondary btn-cta"
                onClick={() => scrollToSection('contact')}
              >
                Contact Us
              </Button>
            </div>
          </div>
          <button 
            className="scroll-indicator"
            onClick={() => scrollToSection('about')}
          >
            <ChevronDown size={32} />
          </button>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="about-section section-padding">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">About the Project</h2>
            <p className="section-subtitle">Innovating sustainability through natural fibre technology</p>
          </div>
          
          <div className="about-content">
            <div className="about-text">
              <div className="about-card">
                <h3 className="card-title">The Problem</h3>
                <p className="card-text">
                  India generates millions of tons of banana stem waste annually after harvesting. 
                  This agricultural residue is often burned or discarded, contributing to environmental 
                  pollution and wasting valuable natural resources.
                </p>
              </div>

              <div className="about-card">
                <h3 className="card-title">Our Solution</h3>
                <p className="card-text">
                  BANASTRA converts this waste into premium quality textile fibre. Our innovative 
                  process extracts strong, lightweight fibres from banana stems, creating sustainable 
                  raw materials for the textile industry while supporting farmer livelihoods.
                </p>
              </div>

              <div className="about-card">
                <h3 className="card-title">Environmental Impact</h3>
                <p className="card-text">
                  By utilizing agricultural waste, we reduce carbon emissions, minimize water usage, 
                  and eliminate harmful chemical processing. Our banana fibre is 100% biodegradable, 
                  renewable, and contributes to a circular economy.
                </p>
              </div>
            </div>

            <div className="about-image">
              <img 
                src="https://images.unsplash.com/photo-1653481006616-aab561a77a3b" 
                alt="Banana Plantation"
                className="rounded-image"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="features-section section-padding">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Benefits & Features</h2>
            <p className="section-subtitle">Why banana fibre is the future of sustainable textiles</p>
          </div>

          <div className="features-grid">
            <Card className="feature-card">
              <div className="feature-icon">
                <Recycle size={48} />
              </div>
              <h3 className="feature-title">Waste Reduction</h3>
              <p className="feature-text">
                Converts 50,000+ tons of agricultural waste annually into valuable textile fibre, 
                preventing pollution and promoting circular economy.
              </p>
            </Card>

            <Card className="feature-card">
              <div className="feature-icon">
                <Leaf size={48} />
              </div>
              <h3 className="feature-title">Low Carbon Footprint</h3>
              <p className="feature-text">
                85% lower CO2 emissions compared to conventional textile production. Natural processing 
                without harmful chemicals or excessive energy consumption.
              </p>
            </Card>

            <Card className="feature-card">
              <div className="feature-icon">
                <Users size={48} />
              </div>
              <h3 className="feature-title">Farmer Income</h3>
              <p className="feature-text">
                Creates additional revenue streams for banana farmers by monetizing previously 
                discarded stems, improving rural livelihoods.
              </p>
            </Card>

            <Card className="feature-card">
              <div className="feature-icon">
                <Droplet size={48} />
              </div>
              <h3 className="feature-title">Water Efficient</h3>
              <p className="feature-text">
                Requires 95% less water than cotton cultivation. Minimal chemical treatment needed, 
                protecting water resources and ecosystems.
              </p>
            </Card>

            <Card className="feature-card">
              <div className="feature-icon">
                <Shield size={48} />
              </div>
              <h3 className="feature-title">Superior Quality</h3>
              <p className="feature-text">
                Strong, lightweight, and naturally moisture-absorbing. Banana fibre is durable, 
                breathable, and has natural antimicrobial properties.
              </p>
            </Card>

            <Card className="feature-card">
              <div className="feature-icon">
                <TrendingUp size={48} />
              </div>
              <h3 className="feature-title">Biodegradable</h3>
              <p className="feature-text">
                100% natural and compostable. Returns to earth without microplastic pollution, 
                unlike synthetic fibres.
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="process" className="process-section section-padding">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Our Process</h2>
            <p className="section-subtitle">From farm waste to sustainable fashion</p>
          </div>

          <div className="process-timeline">
            <div className="process-step">
              <div className="step-number">01</div>
              <div className="step-content">
                <h3 className="step-title">Collection</h3>
                <p className="step-text">
                  Partner farmers supply banana stems post-harvest. We collect stems within 24 hours 
                  to ensure optimal fibre quality.
                </p>
              </div>
              <div className="step-image">
                <img src="https://images.unsplash.com/photo-1653481006620-dbb70963c713" alt="Banana Collection" />
              </div>
            </div>

            <div className="process-step reverse">
              <div className="step-number">02</div>
              <div className="step-content">
                <h3 className="step-title">Fibre Extraction</h3>
                <p className="step-text">
                  Advanced mechanical extraction separates long, strong fibres from the stem. 
                  Our eco-friendly process preserves fibre integrity.
                </p>
              </div>
              <div className="step-image">
                <img src="https://images.unsplash.com/photo-1770122985578-0fc7023eb6e8" alt="Fibre Extraction" />
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">03</div>
              <div className="step-content">
                <h3 className="step-title">Yarn Production</h3>
                <p className="step-text">
                  Extracted fibres are cleaned, processed, and spun into high-quality yarn suitable 
                  for various textile applications.
                </p>
              </div>
              <div className="step-image">
                <img src="https://images.unsplash.com/photo-1766320635268-a7bd54ffb3d9" alt="Yarn Production" />
              </div>
            </div>

            <div className="process-step reverse">
              <div className="step-number">04</div>
              <div className="step-content">
                <h3 className="step-title">Fabric Manufacturing</h3>
                <p className="step-text">
                  Banana fibre yarn is woven or knitted into premium fabrics with excellent drape, 
                  strength, and breathability.
                </p>
              </div>
              <div className="step-image">
                <img src="https://images.unsplash.com/photo-1758270804188-8ca0b6d254bc" alt="Fabric Manufacturing" />
              </div>
            </div>

            <div className="process-step">
              <div className="step-number">05</div>
              <div className="step-content">
                <h3 className="step-title">Sustainable Products</h3>
                <p className="step-text">
                  Final products include eco-friendly clothing, home textiles, and accessories—
                  bringing sustainable fashion to everyday life.
                </p>
              </div>
              <div className="step-image">
                <img src="https://images.unsplash.com/photo-1760043593615-b426cee3755e" alt="Sustainable Fashion" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section id="vision" className="vision-section section-padding">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Vision & Mission</h2>
            <p className="section-subtitle">Building a sustainable textile future</p>
          </div>

          <div className="vision-content">
            <Card className="vision-card">
              <h3 className="vision-card-title">Scalable Model</h3>
              <p className="vision-card-text">
                Develop pan-India network of banana fibre extraction centers, creating sustainable 
                livelihoods in rural areas while addressing agricultural waste management.
              </p>
            </Card>

            <Card className="vision-card">
              <h3 className="vision-card-title">Industry Collaboration</h3>
              <p className="vision-card-text">
                Partner with textile manufacturers, fashion brands, and retailers to mainstream 
                banana fibre as a preferred eco-friendly material.
              </p>
            </Card>

            <Card className="vision-card">
              <h3 className="vision-card-title">Affordable Sustainability</h3>
              <p className="vision-card-text">
                Make sustainable fashion accessible to middle-class families through cost-effective 
                production and competitive pricing without compromising quality.
              </p>
            </Card>
          </div>

          <div className="vision-image">
            <img 
              src="https://images.unsplash.com/photo-1542601906990-b4d3fb778b09" 
              alt="Environmental Care"
              className="full-width-image"
            />
          </div>
        </div>
      </section>

      {/* Gallery Section */}
      <section id="gallery" className="gallery-section section-padding">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Gallery</h2>
            <p className="section-subtitle">Visual journey of our sustainable process</p>
          </div>

          <div className="gallery-grid">
            <div className="gallery-item">
              <img src="https://images.unsplash.com/photo-1643892343594-1e3be700217b" alt="Banana Plantation" />
              <div className="gallery-overlay">
                <p className="gallery-caption">Banana Plantation</p>
              </div>
            </div>

            <div className="gallery-item">
              <img src="https://images.unsplash.com/photo-1552901633-210088e17486" alt="Banana Stems" />
              <div className="gallery-overlay">
                <p className="gallery-caption">Fresh Banana Stems</p>
              </div>
            </div>

            <div className="gallery-item">
              <img src="https://images.unsplash.com/photo-1770122985578-0fc7023eb6e8" alt="Raw Fibres" />
              <div className="gallery-overlay">
                <p className="gallery-caption">Extracted Natural Fibres</p>
              </div>
            </div>

            <div className="gallery-item">
              <img src="https://images.unsplash.com/photo-1768389477120-f1f1ff6dc47c" alt="Natural Fibre" />
              <div className="gallery-overlay">
                <p className="gallery-caption">Premium Fibre Quality</p>
              </div>
            </div>

            <div className="gallery-item">
              <img src="https://images.unsplash.com/photo-1758270804188-8ca0b6d254bc" alt="Textile Production" />
              <div className="gallery-overlay">
                <p className="gallery-caption">Textile Manufacturing</p>
              </div>
            </div>

            <div className="gallery-item">
              <img src="https://images.unsplash.com/photo-1760043593615-b426cee3755e" alt="Sustainable Fashion" />
              <div className="gallery-overlay">
                <p className="gallery-caption">Sustainable Fashion</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact-section section-padding">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Contact Us</h2>
            <p className="section-subtitle">Get in touch to learn more about BANASTRA</p>
          </div>

          <div className="contact-content">
            <div className="contact-info">
              <Card className="info-card">
                <h3 className="info-title">Get In Touch</h3>
                <p className="info-text">
                  Interested in sustainable banana fibre textiles? Whether you're a farmer, 
                  manufacturer, retailer, or conscious consumer, we'd love to hear from you.
                </p>
                
                <div className="contact-details">
                  <div className="contact-item">
                    <strong>Email:</strong>
                    <a href="mailto:banastra555@gmail.com">banastra555@gmail.com</a>
                  </div>
                  <div className="contact-item">
                    <strong>Phone:</strong>
                    <a href="tel:9071403952">9071403952</a>
                  </div>
                </div>

                <div className="sustainability-image">
                  <img 
                    src="https://images.unsplash.com/photo-1528122819723-9dca3a31295d" 
                    alt="Sustainability"
                  />
                </div>
              </Card>
            </div>

            <div className="contact-form-container">
              <Card className="form-card">
                <h3 className="form-title">Send Us a Message</h3>
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-group">
                    <label htmlFor="name" className="form-label">Name</label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      placeholder="Your name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="email" className="form-label">Email</label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      placeholder="your.email@example.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="message" className="form-label">Message</label>
                    <Textarea
                      id="message"
                      name="message"
                      placeholder="Tell us about your interest in banana fibre textiles..."
                      value={formData.message}
                      onChange={handleInputChange}
                      className="form-textarea"
                      rows={5}
                    />
                  </div>

                  <Button type="submit" className="btn-primary btn-submit">
                    Send Message
                  </Button>
                </form>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-logo">
              <img src={LOGO_URL} alt="Banastra Logo" />
            </div>
            <div className="footer-text">
              <p>© 2024 BANASTRA - Natural Fibers Reimagined</p>
              <p>Sustainable textiles from agricultural waste</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
